import { useCallback, useEffect, useRef, useState } from 'react';
import type { Page, PageWorkspaceLayout } from '@/components/canvas/document/publicTypes';

const DRAG_CLICK_THRESHOLD = 4;

export interface UsePageDragOptions {
  zoom: number;
  disabled?: boolean;
  isPanModeActive: boolean;
  currentPageId: string;
  setCurrentPageId: (id: string) => void;
  setEditingTextId: (id: string | null) => void;
  setEditingGroupId: (id: string | null) => void;
  bringPageToFront: (pageId: string) => void;
  selectPageFrame: () => void;
  pushHistory: () => void;
  updatePageWorkspaceLayoutLive: (pageId: string, patch: Partial<PageWorkspaceLayout>) => void;
  updatePageWorkspaceLayout: (pageId: string, patch: Partial<PageWorkspaceLayout>) => void;
  getPageById: (pageId: string) => Page | undefined;
}

export function usePageDrag({
  zoom,
  disabled = false,
  isPanModeActive,
  currentPageId,
  setCurrentPageId,
  setEditingTextId,
  setEditingGroupId,
  bringPageToFront,
  selectPageFrame,
  pushHistory,
  updatePageWorkspaceLayoutLive,
  updatePageWorkspaceLayout,
  getPageById,
}: UsePageDragOptions) {
  const [draggingPageId, setDraggingPageId] = useState<string | null>(null);
  const dragRef = useRef<{
    pageId: string;
    startClientX: number;
    startClientY: number;
    startX: number;
    startY: number;
  } | null>(null);
  const movedRef = useRef(false);

  const activatePage = useCallback(
    (pageId: string) => {
      if (pageId !== currentPageId) {
        setCurrentPageId(pageId);
        setEditingTextId(null);
        setEditingGroupId(null);
      }
      selectPageFrame();
      bringPageToFront(pageId);
    },
    [
      currentPageId,
      setCurrentPageId,
      selectPageFrame,
      setEditingTextId,
      setEditingGroupId,
      bringPageToFront,
    ],
  );

  /** 开始拖拽移动页面（不清除页面选框） */
  const startPageDrag = useCallback(
    (pageId: string, e: React.MouseEvent) => {
      if (disabled || e.button !== 0 || isPanModeActive) return;
      e.stopPropagation();
      e.preventDefault();

      const page = getPageById(pageId);
      if (!page) return;

      if (pageId !== currentPageId) {
        setCurrentPageId(pageId);
        setEditingTextId(null);
        setEditingGroupId(null);
      }
      bringPageToFront(pageId);
      pushHistory();
      movedRef.current = false;
      dragRef.current = {
        pageId,
        startClientX: e.clientX,
        startClientY: e.clientY,
        startX: page.workspaceLayout.x,
        startY: page.workspaceLayout.y,
      };
      setDraggingPageId(pageId);
    },
    [
      isPanModeActive,
      disabled,
      getPageById,
      currentPageId,
      setCurrentPageId,
      setEditingTextId,
      setEditingGroupId,
      bringPageToFront,
      pushHistory,
    ],
  );

  const handleChromeMouseDown = startPageDrag;

  const handleChromeClick = useCallback(
    (pageId: string, e: React.MouseEvent) => {
      e.stopPropagation();
      if (movedRef.current) {
        movedRef.current = false;
        return;
      }
      activatePage(pageId);
    },
    [activatePage],
  );

  useEffect(() => {
    if (!dragRef.current) return;

    const onMove = (e: MouseEvent) => {
      const drag = dragRef.current;
      if (!drag) return;
      const dx = (e.clientX - drag.startClientX) / zoom;
      const dy = (e.clientY - drag.startClientY) / zoom;
      if (Math.abs(dx) > DRAG_CLICK_THRESHOLD || Math.abs(dy) > DRAG_CLICK_THRESHOLD) {
        movedRef.current = true;
      }
      updatePageWorkspaceLayoutLive(drag.pageId, {
        x: Math.round(drag.startX + dx),
        y: Math.round(drag.startY + dy),
      });
    };

    const onUp = () => {
      const drag = dragRef.current;
      dragRef.current = null;
      setDraggingPageId(null);
      if (!drag) return;
      const page = getPageById(drag.pageId);
      if (!page) return;
      if (movedRef.current) {
        updatePageWorkspaceLayout(drag.pageId, {
          x: page.workspaceLayout.x,
          y: page.workspaceLayout.y,
        });
      } else {
        // mousedown 上 preventDefault 会吞掉 click，此处补全「点页码激活 + 页框选中」
        activatePage(drag.pageId);
      }
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [
    draggingPageId,
    zoom,
    updatePageWorkspaceLayoutLive,
    updatePageWorkspaceLayout,
    getPageById,
    activatePage,
  ]);

  return {
    draggingPageId,
    startPageDrag,
    handleChromeMouseDown,
    handleChromeClick,
  };
}
